import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { BookOpen, Search, ArrowRight, Tag } from "lucide-react";
import api from "../../lib/api";
import useSeo from "../../hooks/useSeo";

export default function BlogList() {
    const { i18n } = useTranslation();
    const lang = (i18n.language || "tr").split("-")[0];
    const [posts, setPosts] = useState([]);
    const [categories, setCategories] = useState([]);
    const [meta, setMeta] = useState({ page: 1, total: 0, pages: 1 });
    const [loading, setLoading] = useState(true);
    const [searchParams, setSearchParams] = useSearchParams();

    const activeCat = searchParams.get("category") || "";
    const search = searchParams.get("q") || "";
    const page = parseInt(searchParams.get("page") || "1", 10);
    const [searchInput, setSearchInput] = useState(search);

    useSeo({
        title: "Blog · Sektör Yazıları",
        description: "Otel sektörü analizleri, yatırım rehberleri, mevzuat değişiklikleri ve sürdürülebilirlik konularında uzman içerikler.",
        path: "/blog",
    });

    useEffect(() => {
        api.get("/blog/categories").then(({ data }) => setCategories(data.items || [])).catch(() => { });
    }, []);

    useEffect(() => {
        setLoading(true);
        const params = new URLSearchParams({ page: String(page) });
        if (activeCat) params.set("category", activeCat);
        if (search) params.set("search", search);
        api.get(`/blog?${params.toString()}`)
            .then(({ data }) => {
                setPosts(data.items || []);
                setMeta({ page: data.page, total: data.total, pages: data.pages });
            })
            .catch(() => { })
            .finally(() => setLoading(false));
    }, [activeCat, search, page]);

    const tr = (obj) => (obj && (obj[lang] || obj.tr)) || "";

    const setCat = (slug) => {
        const next = new URLSearchParams(searchParams);
        if (slug) next.set("category", slug);
        else next.delete("category");
        next.delete("page");
        setSearchParams(next);
    };

    const submitSearch = (e) => {
        e.preventDefault();
        const next = new URLSearchParams(searchParams);
        if (searchInput.trim()) next.set("q", searchInput.trim());
        else next.delete("q");
        next.delete("page");
        setSearchParams(next);
    };

    const goPage = (p) => {
        const next = new URLSearchParams(searchParams);
        next.set("page", String(p));
        setSearchParams(next);
    };

    return (
        <div className="bg-light-bg min-h-[80vh]" data-testid="blog-list-page">
            <section className="bg-dark-navy text-white relative overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-br from-[#08111d] via-[#0c2438] to-[#123b52]" />
                <div className="relative max-w-5xl mx-auto px-6 py-12 lg:py-16">
                    <span className="text-xs uppercase tracking-[0.25em] text-teal font-semibold">Yayın</span>
                    <h1 className="mt-3 text-4xl sm:text-5xl font-bold tracking-tight inline-flex items-center gap-3">
                        <BookOpen className="w-8 h-8 text-gold" /> Sektör Yazıları
                    </h1>
                    <p className="mt-3 text-white/75 max-w-2xl">
                        Sektör analizleri, yatırım rehberleri, mevzuat ve sürdürülebilirlik konularında uzman içerikler.
                    </p>
                    <form onSubmit={submitSearch} className="mt-6 flex items-stretch max-w-md rounded-md overflow-hidden border border-white/15 bg-white/5">
                        <input
                            type="text"
                            value={searchInput}
                            onChange={(e) => setSearchInput(e.target.value)}
                            placeholder="Yazı ara..."
                            className="flex-1 bg-transparent text-white placeholder:text-white/50 px-4 py-3 text-sm outline-none"
                            data-testid="blog-search-input"
                        />
                        <button type="submit" className="bg-gold hover:bg-gold-700 text-dark-navy font-bold px-5 inline-flex items-center gap-2" data-testid="blog-search-submit">
                            <Search className="w-4 h-4" />
                        </button>
                    </form>
                </div>
            </section>

            <section className="max-w-6xl mx-auto px-6 py-10">
                <div className="flex items-center gap-2 flex-wrap mb-8" data-testid="blog-category-tabs">
                    <button
                        onClick={() => setCat("")}
                        className={`px-3 py-1.5 rounded-md text-sm font-semibold ${!activeCat ? "bg-dark-navy text-white" : "bg-white text-deep-blue border border-slate-200 hover:border-teal/40"}`}
                        data-testid="blog-cat-all"
                    >
                        Tümü
                    </button>
                    {categories.map((c) => (
                        <button
                            key={c.id}
                            onClick={() => setCat(c.slug)}
                            data-testid={`blog-cat-${c.slug}`}
                            className={`px-3 py-1.5 rounded-md text-sm font-semibold ${activeCat === c.slug ? "bg-dark-navy text-white" : "bg-white text-deep-blue border border-slate-200 hover:border-teal/40"}`}
                        >
                            {tr(c.name)}
                        </button>
                    ))}
                </div>

                {loading ? (
                    <div className="text-center text-deep-blue/60 text-sm py-12">Yükleniyor...</div>
                ) : posts.length === 0 ? (
                    <div className="text-center text-deep-blue/60 text-sm py-12">Bu kriterlere uygun yazı bulunamadı.</div>
                ) : (
                    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="blog-list-grid">
                        {posts.map((p) => (
                            <Link
                                key={p.id}
                                to={`/blog/${p.slug}`}
                                data-testid={`blog-card-${p.slug}`}
                                className="lift-card group bg-white rounded-lg overflow-hidden shadow-md hover:shadow-xl border border-slate-100"
                            >
                                <div className="aspect-[16/9] bg-light-bg relative overflow-hidden">
                                    {p.featured_image ? (
                                        <img src={p.featured_image} alt={tr(p.title)} className="w-full h-full object-cover group-hover:scale-105 transition-transform" />
                                    ) : (
                                        <div className="w-full h-full bg-gradient-to-br from-deep-blue to-dark-navy flex items-center justify-center text-white/30">
                                            <BookOpen className="w-10 h-10" />
                                        </div>
                                    )}
                                </div>
                                <div className="p-5">
                                    {p.category_name && (
                                        <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-teal/10 text-teal-700 font-semibold">
                                            <Tag className="w-3 h-3" /> {tr(p.category_name)}
                                        </span>
                                    )}
                                    <h3 className="mt-2 text-base font-bold text-dark-navy line-clamp-2">{tr(p.title)}</h3>
                                    <p className="mt-1 text-sm text-deep-blue/70 line-clamp-2">{tr(p.excerpt)}</p>
                                    <div className="mt-3 flex items-center justify-between text-xs text-deep-blue/60">
                                        <span className="font-data">{p.published_at ? new Date(p.published_at).toLocaleDateString("tr-TR") : ""}</span>
                                        <span>{p.reading_time || 4} dk okuma</span>
                                    </div>
                                </div>
                            </Link>
                        ))}
                    </div>
                )}

                {meta.pages > 1 && (
                    <div className="mt-10 flex items-center justify-center gap-2" data-testid="blog-pagination">
                        {Array.from({ length: meta.pages }).map((_, i) => (
                            <button
                                key={i}
                                onClick={() => goPage(i + 1)}
                                className={`w-10 h-10 rounded-md text-sm font-semibold ${meta.page === i + 1 ? "bg-dark-navy text-white" : "bg-white text-deep-blue border border-slate-200 hover:border-teal/40"}`}
                            >
                                {i + 1}
                            </button>
                        ))}
                    </div>
                )}
            </section>
        </div>
    );
}
